import 'react-dropzone-uploader/dist/styles.css';
import React from 'react';
import DropZone, {IFileWithMeta, IUploadParams, StatusValue} from 'react-dropzone-uploader';
import * as R from 'ramda';

const MAX_AD_SIZE_BYTES = 31457280 // 30MB (base 2)

type Props = {
  userIdToken: string,
  adFileKey: string,
  canRemove?: boolean,
  initialfile?: File,
  onDone?: (_: IFileWithMeta) => void,
  onRemove?: (_: IFileWithMeta) => void
};

// toArray :: a|undefined|null -> [a]
function toArray<T>(x: T): [T] {
  return x === undefined || x === null
  ? [] as unknown as [T]
  : [x];
}

function signAdUpload(userToken: string, fileKey: string, {file, meta}: IFileWithMeta): Promise<IUploadParams> {
  console.debug('File metadata: %o', meta);
  const postDataUrl = `/api/v1/requestfiles/upload?adFileKey=${encodeURIComponent(fileKey)}`;
  return fetch(postDataUrl, {
    headers: {Authorization: userToken}
  }).then(async response => {
    if (response.status === 401 || response.status === 403) {
      // returning an object without a url property gets
      // React Dropzone Uploader to call handleChangeStatus(status = 'error_upload_params')
      return {} as IUploadParams;
    } else {
      return await response.json()
        .then(R.assocPath(['fields', 'Content-Type'], file.type));
    }
  }).catch(err => {
    switch (err) {
      case 'unauthorized':
        console.error('Unauthorized request was rejected');
        break;

      default:
        console.error('Unable to get upload params: %o', err);
        break;
    }
    return Promise.reject(err);
  });
}

const AdUploader: React.FC<Props> = ({userIdToken, adFileKey, initialfile, canRemove = false, onDone, onRemove}: Props) => {
  function handleChangeStatus(fileWithMeta: IFileWithMeta, status: StatusValue): void {
    switch (status) {
      case 'done':
        if (onDone) {
          onDone(fileWithMeta);
        }
        break;

      case 'error_upload_params':
        console.error('Unable to get upload params for file %o', fileWithMeta);
        // Can't get enough info out of React Dropzone Uploader
        // to make a determination as to whether this is
        // an authentication error or a different error.
        alert('Unable to upload your file. You may have left the application for too long. Please reload the page and try again.');
        break;

      case 'rejected_file_type':
        alert('That ad file is unsupported. Make sure you are uploading one of the valid ad file types.');
        break;

      case 'removed':
        if (onRemove) {
          onRemove(fileWithMeta)
        }
        break;
    }
  }

  return <DropZone
    initialFiles={toArray(initialfile) as [File]}
    getUploadParams={R.partial(signAdUpload, [userIdToken, adFileKey])}
    onChangeStatus={handleChangeStatus}
    multiple={false}
    canRestart={false}
    canCancel={false}
    canRemove={canRemove}
    maxFiles={1}
    styles={{dropzone: {minHeight: 200, maxHeight: 250}}}
    maxSizeBytes={MAX_AD_SIZE_BYTES}
    accept=".jpg, .jpeg, .png, .ppt, .pptx, .mp4"
  />
};
export default AdUploader;
